import { toQueryString } from 'lib/string';

const SCRIPT_LOAD_TIMEOUT = 10000;

enum State {
  Unloaded,
  Loading,
  Loaded,
  Failed,
}

class Script {
  private promise: Promise<any>;
  private promiseResolve: Function;
  private promiseReject: Function;
  private initializer?: Function | boolean;
  private result?: any;
  private state: State;
  private urlOrFn: string | (() => string);
  private timeoutId?: ReturnType<typeof setTimeout>;
  private attributes?: Record<string, string>;

  constructor(
    urlOrFn: string | (() => string),
    initializer?,
    attributes?: Record<string, string>
  ) {
    this.initializer = initializer;
    this.urlOrFn = urlOrFn;
    this.state = State.Unloaded;
    this.attributes = attributes;
  }

  private getUrl(): string {
    return typeof this.urlOrFn === 'function' ? this.urlOrFn() : this.urlOrFn;
  }

  load() {
    if (this.state === State.Unloaded) {
      this.promise = new Promise<any>((resolve, reject) => {
        this.promiseResolve = resolve;
        this.promiseReject = reject;
        this.state = State.Loading;

        const script = document.createElement('script');
        script.addEventListener('load', this.afterLoad.bind(this));
        script.addEventListener('error', this.onError.bind(this));

        if (this.attributes) {
          Object.entries(this.attributes).forEach(([key, value]) => {
            script.setAttribute(key, value);
          });
        }

        const url = this.getUrl();
        script.src = url;
        document.head.appendChild(script);

        this.timeoutId = setTimeout(() => {
          if (this.state === State.Loading) {
            this.state = State.Failed;
            this.promiseReject(new Error('Script load timeout'));
          }
        }, SCRIPT_LOAD_TIMEOUT);
      });
    }
    return this.promise;
  }

  private afterLoad() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.state = State.Loaded;
    if (!this.initializer) {
      this.setInitialized();
    } else if (typeof this.initializer === 'function') {
      this.initializer(this);
    }
  }

  private onError() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.state = State.Failed;
    this.promiseReject(new Error('Script failed to load'));
  }

  setInitialized() {
    if (this.promiseResolve) {
      this.promiseResolve(this.result);
      delete this.promiseResolve;
      delete this.promiseReject;
      delete this.result;
    }
  }
}

function getGoogleMapsApiKey(): string {
  if (window.gon?.googleMapsApiKey) {
    return window.gon.googleMapsApiKey;
  }

  const metaTag = document.querySelector('meta[itemprop="google-maps-api-key"]');
  if (metaTag) {
    return metaTag.getAttribute('content') || '';
  }

  return '';
}

function googleMapsUrl(): string {
  const apiKey = getGoogleMapsApiKey();

  if (!apiKey) {
    console.warn('Google Maps API key not found'); // eslint-disable-line no-console
  }

  const params = toQueryString({
    callback: 'googleMapsInitCallback',
    key: apiKey,
    language: 'en',
    libraries: 'places',
  });
  return `https://maps.googleapis.com/maps/api/js?${params}`;
}

function googleMapsInitCallback() {
  GoogleMaps.setInitialized();
}

export const GoogleMaps = new Script(googleMapsUrl, true);

window.googleMapsInitCallback = googleMapsInitCallback;
